/**
 * Detects the user's country code (ISO 3166-1 alpha-2) from device settings,
 * prioritizing OS/browser timezone and locale, falling back to 'US'.
 */
export const getUserDeviceRegion = (): string => {
  try {
    if (typeof window !== "undefined") {
      // 1. Check Intl DateTimeFormat timezone (most reliable geo signal from the OS)
      if (typeof Intl !== "undefined" && Intl.DateTimeFormat) {
        try {
          const timeZone =
            Intl.DateTimeFormat().resolvedOptions().timeZone || "";
          if (
            timeZone === "Asia/Calcutta" ||
            timeZone === "Asia/Kolkata" ||
            timeZone.includes("Calcutta") ||
            timeZone.includes("Kolkata")
          ) {
            return "IN";
          }
          if (
            timeZone.startsWith("America/New_York") ||
            timeZone.startsWith("America/Los_Angeles") ||
            timeZone.startsWith("America/Chicago") ||
            timeZone.startsWith("America/Denver") ||
            timeZone.startsWith("America/Phoenix") ||
            timeZone.startsWith("US/")
          ) {
            return "US";
          }
          if (timeZone.startsWith("Europe/London")) {
            return "GB";
          }
          if (timeZone.startsWith("Asia/Tokyo")) {
            return "JP";
          }
          if (timeZone.startsWith("Australia/")) {
            return "AU";
          }
          if (
            timeZone.startsWith("America/Toronto") ||
            timeZone.startsWith("America/Vancouver") ||
            timeZone.startsWith("America/Montreal")
          ) {
            return "CA";
          }
          if (timeZone.startsWith("Europe/Berlin")) {
            return "DE";
          }
          if (timeZone.startsWith("Europe/Paris")) {
            return "FR";
          }
        } catch {
          // Fall through
        }

        // 2. Check Intl DateTimeFormat resolved locale (e.g. 'en-IN', 'en-US')
        try {
          const intlLocale =
            Intl.DateTimeFormat().resolvedOptions().locale || "";
          const parts = intlLocale.split(/[-_]/);
          if (parts.length > 1 && parts[parts.length - 1].length === 2) {
            return parts[parts.length - 1].toUpperCase();
          }
        } catch {
          // Fall through
        }
      }

      // 3. Check navigator.languages list
      if (window.navigator) {
        const languages =
          window.navigator.languages || [window.navigator.language];
        for (const lang of languages) {
          if (!lang) continue;
          const parts = lang.split(/[-_]/);
          if (parts.length > 1 && parts[parts.length - 1].length === 2) {
            return parts[parts.length - 1].toUpperCase();
          }
        }
      }
    }
  } catch (err) {
    console.warn("Error detecting user device region:", err);
  }

  return "US";
};

/**
 * Maps a streaming provider name to its corresponding platform search URL
 * for the given movie title, with a graceful fallback link.
 */
export const getProviderSearchUrl = (
  providerName: string,
  movieTitle: string,
  fallbackLink?: string,
): string => {
  const name = providerName.toLowerCase();
  const query = encodeURIComponent(movieTitle);

  if (name.includes("netflix")) {
    return `https://www.netflix.com/search?q=${query}`;
  }
  if (name.includes("amazon") || name.includes("prime video")) {
    return `https://www.primevideo.com/search/ref=atv_nb_sr?phrase=${query}`;
  }
  if (
    name.includes("hotstar") ||
    name.includes("disney+") ||
    name.includes("disney plus")
  ) {
    return `https://www.hotstar.com/in/explore?search_query=${query}`;
  }
  if (name.includes("apple tv") || name.includes("itunes")) {
    return `https://tv.apple.com/search?term=${query}`;
  }
  if (name.includes("google play") || name.includes("google tv")) {
    return `https://play.google.com/store/search?q=${query}&c=movies`;
  }
  if (name.includes("youtube")) {
    return `https://www.youtube.com/results?search_query=${encodeURIComponent(
      movieTitle + " full movie",
    )}`;
  }
  if (name.includes("jiocinema") || name.includes("jio cinema")) {
    return `https://www.jiocinema.com/search/${query}`;
  }
  if (name.includes("zee5")) {
    return `https://www.zee5.com/search?q=${query}`;
  }
  if (name.includes("sonyliv") || name.includes("sony liv")) {
    return `https://www.sonyliv.com/search/${query}`;
  }
  if (name.includes("lionsgate")) {
    return `https://www.lionsgateplay.com/search?q=${query}`;
  }
  if (name.includes("aha")) {
    return `https://www.aha.video/search?q=${query}`;
  }
  if (name.includes("sun nxt")) {
    return `https://www.sunnxt.com/search/${query}`;
  }
  if (name.includes("hulu")) {
    return `https://www.hulu.com/search?q=${query}`;
  }
  if (name.includes("max") || name.includes("hbo")) {
    return `https://www.max.com/search?q=${query}`;
  }
  if (name.includes("paramount")) {
    return `https://www.paramountplus.com/search/?q=${query}`;
  }
  if (name.includes("peacock")) {
    return `https://www.peacocktv.com/search?q=${query}`;
  }
  if (name.includes("crunchyroll")) {
    return `https://www.crunchyroll.com/search?q=${query}`;
  }
  if (name.includes("tubi")) {
    return `https://tubitv.com/search/${query}`;
  }
  if (name.includes("pluto")) {
    return `https://pluto.tv/search/details/movies/${query}`;
  }

  // Fallback to TMDB / JustWatch watch link if provided, otherwise Google search
  return (
    fallbackLink ||
    `https://www.google.com/search?q=${encodeURIComponent(
      `${movieTitle} watch on ${providerName}`,
    )}`
  );
};
